import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { AuthService } from '../../../auth/services/auth.service';
import { ProfileNotificationsActions } from '../../../notification-center/store/actions/profileNotifications.action';
import {
    selectCurrentPlayerAvatar, selectCurrentPlayerExp,
    selectCurrentPlayerId,
    selectCurrentPlayerNickname,
} from '../../../profile/store/selectors/profileGeneral.selector';
import {
    selectCurrentPlayerPaymentAccountBalanceValue,
    selectCurrentPlayerPaymentAccountLPBalanceValue,
} from '../../../wallet/store/selectors/wallet.selector';

import { CdkConnectedOverlay, CdkOverlayOrigin } from '@angular/cdk/overlay';
import { NgClass, NgOptimizedImage } from '@angular/common';
import { MatRipple } from '@angular/material/core';
import { ResponseImageSetPipe } from '../../pipes/response-image-set.pipe';
import { TranslatePipe } from '@ngx-translate/core';
import { SvgIconComponent } from 'angular-svg-icon';
import { PushNotificationService } from '../../services/push-notification.service';

@Component({
    selector: 'app-edit-user-profile',
    templateUrl: './edit-user-profile.component.html',
    styleUrls: ['./edit-user-profile.component.scss'],
    imports: [
        CdkOverlayOrigin,
        MatRipple,
        NgClass,
        CdkConnectedOverlay,
        ResponseImageSetPipe,
        TranslatePipe,
        SvgIconComponent,
        NgOptimizedImage,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EditUserProfileComponent {
    private store = inject(Store);
    private authService = inject(AuthService);
    private router = inject(Router);
    private pushNotificationsService = inject(PushNotificationService);

    avatar = this.store.selectSignal(selectCurrentPlayerAvatar);
    nickname = this.store.selectSignal(selectCurrentPlayerNickname);
    profileId = this.store.selectSignal(selectCurrentPlayerId);
    balance = this.store.selectSignal(selectCurrentPlayerPaymentAccountBalanceValue);
    balanceLPSignal = this.store.selectSignal(selectCurrentPlayerPaymentAccountLPBalanceValue);
    isOpen = signal(false);
    currentUserExp = this.store.selectSignal(selectCurrentPlayerExp);

    isAuthenticatedSig = toSignal(this.authService.isAuthenticated$, { initialValue: this.authService.isAuthorised });

    isAuthorised = computed(() => this.isAuthenticatedSig());

    isPlayer = computed(() => {
        return this.isAuthenticatedSig() && this.authService.isPlayer;
    });

    isEmployee = computed(() => {
        return this.isAuthenticatedSig() && this.authService.isEmployee;
    });

    isOperator = computed(() => {
        return this.isAuthenticatedSig() && this.authService.isOperator;
    });

    switchUserProfileSettingsVisibility(): void {
        this.isOpen.update((v) => !v);
    }

    openProfileWallet(): void {
        this.router.navigate(['/wallet']);
        this.switchUserProfileSettingsVisibility();
    }

    logOut() {
        this.authService.logout();
        this.store.dispatch(ProfileNotificationsActions.clearProfileNotificationsList());
        this.pushNotificationsService.detachNotifications();
    }


    openProfile() {
        if (this.isPlayer()) {
            const profileId = this.profileId();
            if (profileId) {
                this.router.navigate(['profile', profileId]);
            }

            this.isOpen.set(false);
        }
    }

    openProfileMyTournaments() {
        const profileId = this.profileId();
        if (profileId) {
            this.router.navigateByUrl(`profile/${profileId}/tournaments`);
        }

        this.isOpen.set(false);
    }

    openProfileSettings() {
        const profileId = this.profileId();
        if (profileId) {
            this.router.navigateByUrl(`profile/${profileId}/settings`);
        }

        this.isOpen.set(false);
    }

    openProfileMyTeams() {
        const profileId = this.profileId();
        if (profileId) {
            this.router.navigateByUrl(`profile/${profileId}/teams`);
        }

        this.isOpen.set(false);
    }

    openProfileMyProfile() {
        const profileId = this.profileId();
        if (profileId) {
            this.router.navigateByUrl(`profile/${profileId}`);
        }

        this.isOpen.set(false);
    }
}
